import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  computed,
  effect,
  input,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import type { BillingPlan } from '@tourfold/libraries/tf-api-client-frontend-v2';

@Component({
  selector: 'tf-plan-change-dialog',
  imports: [CommonModule, TranslateModule, DialogModule, ButtonModule],
  templateUrl: './plan-change-dialog.html',
  styleUrl: './plan-change-dialog.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class PlanChangeDialog {
  readonly open = input.required<boolean>();
  readonly currentPlan = input<BillingPlan | null>(null);
  readonly targetPlan = input<BillingPlan | null>(null);
  readonly currentPrice = input<string | null>(null);
  readonly currentPriceSuffix = input<string | null>(null);
  readonly targetPrice = input<string | null>(null);
  readonly targetPriceSuffix = input<string | null>(null);
  readonly requiresBillingTerms = input<boolean>(false);
  readonly isConfirming = input<boolean>(false);

  readonly closed = output<void>();
  readonly confirmed = output<BillingPlan>();

  protected readonly consentAccepted = signal(false);

  protected readonly canConfirm = computed(() => {
    if (this.isConfirming() || !this.targetPlan()) {
      return false;
    }
    return !this.requiresBillingTerms() || this.consentAccepted();
  });

  private wasOpen = false;

  constructor() {
    effect(() => {
      const isOpen = this.open();
      if (isOpen && !this.wasOpen) {
        this.consentAccepted.set(false);
      }
      this.wasOpen = isOpen;
    });
  }

  protected onDialogVisibleChange(visible: boolean): void {
    if (!visible) {
      this.closed.emit();
    }
  }

  protected onConsentChange(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    this.consentAccepted.set(target?.checked === true);
  }

  protected isTranslationKey(value: string): boolean {
    return value.startsWith('SETTINGS.') || value.startsWith('SETTINGS_');
  }

  protected onConfirm(): void {
    const plan = this.targetPlan();
    if (!plan || !this.canConfirm()) {
      return;
    }
    this.confirmed.emit(plan);
  }

  protected onCancel(): void {
    this.closed.emit();
  }
}
